"use client";

import { memo, useCallback } from "react";
import Link from "next/link";
import type { Tool } from "@/lib/types";
import { useAuth } from "@/lib/context/AuthProvider";
import { useLocale } from "@/lib/context/LocaleProvider";
import { useUIActions } from "@/lib/context/UIProvider";
import { preloadProModal } from "@/components/layout/modalPreload";
import { IconTile } from "@/components/ui/ToolIcon";
import { Badge } from "@/components/ui/Badge";

/** Tool tile used in the Tools grid, related rails, and item detail. */
function ToolCardBase({ tool }: { tool: Tool }) {
  const { isPro, hydrated } = useAuth();
  const { locale, hydrated: localeHydrated } = useLocale();
  const { openProModal } = useUIActions();
  // Until `/me` resolves `isPro` is false for everyone, so gating on it here
  // would bounce paying users into the upsell. Let the link through instead.
  const needsPro = hydrated && tool.isPro && !isPro;

  // The Pro modal is a lazy chunk that also fetches its price on mount, so an
  // unwarmed click pays for both before anything appears. Hover/focus buys a
  // few hundred ms; pointerdown still beats the click event on touch.
  const warm = useCallback(() => {
    if (!needsPro) return;
    preloadProModal(localeHydrated ? locale : undefined);
  }, [needsPro, locale, localeHydrated]);

  return (
    <Link
      href={`/tools/${tool.slug}`}
      // Default prefetch only: it warms the route's `loading.tsx` boundary,
      // which is all the transition needs. Forcing full prefetch here would
      // fire an RSC request for every card in the viewport.
      onPointerEnter={warm}
      onPointerDown={warm}
      onFocus={warm}
      onClick={(e) => {
        if (!needsPro) return;
        e.preventDefault();
        openProModal();
      }}
      className="card-lift flex cursor-pointer flex-col gap-2.5 rounded-[20px] border border-[rgba(26,24,22,0.05)] bg-surface px-[18px] pt-[18px] pb-4"
    >
      <div className="flex items-start justify-between gap-2">
        <IconTile category={tool.category} icon={tool.icon} size="md" />
        {tool.isPro ? <Badge variant="pro" size="md" /> : null}
      </div>
      <div className="text-[10.5px] font-semibold uppercase tracking-[0.6px] text-muted">
        {tool.category}
      </div>
      <div className="text-[16.5px] font-semibold leading-[1.2] tracking-[-0.2px] text-ink">
        {tool.name}
      </div>
      <div className="text-[13px] leading-[1.45] text-ink-3">{tool.desc}</div>
    </Link>
  );
}

/**
 * Memoized: the grid renders dozens of these and `tool` objects are stable
 * between renders, so unrelated parent updates shouldn't re-render them all.
 */
export const ToolCard = memo(ToolCardBase);
